import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Feather, MapPin, Heart, MessageCircle, Filter, ArrowUpDown,
  BookOpen, Trophy, Lightbulb, Mail, Clock,
} from 'lucide-react';
import { axiosInstance } from '../../services/api';
import { getStoryEngagement } from '../../services/reactionService';
import { getUser } from '../../utils/secureStorage';
import { focalPosition, getMosaicTemplate, getCategoryConfig, groupByTimeBucket } from '../../utils/mediaLayout';
import { getMediaServeUrl } from '../../config/mediaAPI';
import IdentifyVoicesQueue from '../videoactivation/IdentifyVoicesQueue';
import WisdomMomentsFeed from '../videoactivation/WisdomMomentsFeed';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

function resolveUrl(m) {
  if (m.file_url && m.file_url.startsWith('http')) return m.file_url;
  if (m.url && m.url.startsWith('http')) return m.url;
  if (m.url && m.url.startsWith('/')) return `${API_ORIGIN}${m.url}`;
  if (m.thumbnail_url && m.thumbnail_url.startsWith('/')) return `${API_ORIGIN}${m.thumbnail_url}`;
  if (m.thumbnail_url) return m.thumbnail_url;
  if (m.id && m.id !== 'thumb') return getMediaServeUrl(m.id);
  return '';
}

/* ─── Greeting ─── */
function FeedGreeting({ firstName }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 34, color: '#2C2F26', margin: 0, lineHeight: 1.1 }}>
        Welcome back, {firstName || 'Friend'}
      </h1>
      <p style={{ fontFamily: FONT_B, fontSize: 16, color: '#7C846A', marginTop: 6 }}>
        Here's what's happening with your family
      </p>
    </div>
  );
}

/* ─── Share Prompt ─── */
function SharePromptBar({ onShare }) {
  return (
    <button onClick={onShare} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
      background: '#fff', border: '1px solid #E9E6D6', borderRadius: 999,
      padding: '10px 12px 10px 20px', cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(54,60,40,.06)', marginBottom: 28,
    }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#FFF0E0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Feather size={20} color="#E65100" />
      </div>
      <span style={{ flex: 1, textAlign: 'left', fontSize: 16, color: '#9AA088', fontFamily: FONT_B }}>
        Share a memory, photo, or life lesson…
      </span>
      <span style={{
        background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14,
        padding: '13px 24px', borderRadius: 999, fontFamily: FONT_B, whiteSpace: 'nowrap',
        boxShadow: '0 4px 12px rgba(85,107,47,.26)',
      }}>
        Share a Story
      </span>
    </button>
  );
}

/* ─── Section Toolbar ─── */
function FeedToolbar({ label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0 }}>{label}</h2>
        {count > 0 && (
          <span style={{ background: '#E7EEDA', color: '#556B2F', fontWeight: 700, fontSize: 13, padding: '3px 10px', borderRadius: 999, fontFamily: FONT_B }}>{count} new</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #E6E4D8', color: '#5A6150', fontWeight: 600, fontSize: 14, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B, minHeight: 44 }}>
          <Filter size={15} /> Filter
        </button>
        <button style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #E6E4D8', color: '#5A6150', fontWeight: 600, fontSize: 14, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT_B, minHeight: 44 }}>
          <ArrowUpDown size={15} /> Newest
        </button>
      </div>
    </div>
  );
}

/* ─── Category Badge ─── */
function CategoryBadge({ category }) {
  const config = getCategoryConfig(category);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: config.bg, color: config.fg,
      fontWeight: 700, fontSize: 12.5, padding: '6px 13px', borderRadius: 999,
      fontFamily: FONT_B, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {config.label}
    </span>
  );
}

/* ─── Media Mosaic ─── */
function MosaicPreview({ media }) {
  if (!media || media.length === 0) return null;
  const template = getMosaicTemplate(media.length);
  const gap = 3;
  const coverS = (m) => ({
    width: '100%', height: '100%', objectFit: 'cover',
    objectPosition: focalPosition(m), display: 'block',
  });

  if (template.type === 'single') {
    return (
      <div style={{ width: '100%', maxHeight: 520, overflow: 'hidden', background: '#F5F5DC' }}>
        <img src={resolveUrl(media[0])} alt="" style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 520, objectFit: 'contain', objectPosition: 'center' }} loading="lazy" decoding="async" />
      </div>
    );
  }
  if (template.type === 'duo') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap, height: 320 }}>
        {media.slice(0, 2).map((m, i) => <div key={m.id || i} style={{ overflow: 'hidden' }}><img src={resolveUrl(m)} alt="" style={coverS(m)} loading="lazy" decoding="async" /></div>)}
      </div>
    );
  }
  // trio and quad: left image drives the height via aspect-ratio, right column fills with cover
  const rightMedia = template.type === 'trio' ? media.slice(1, 3) : media.slice(1, 3);
  const remainder = template.type === 'quad' ? template.remainder : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap, minHeight: 280, maxHeight: 520 }}>
      {/* Left — lead image, natural aspect, no crop */}
      <div style={{ gridRow: 'span 2', overflow: 'hidden', background: '#F0EFE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={resolveUrl(media[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', display: 'block' }} loading="lazy" decoding="async" />
      </div>
      {/* Right column — cover-cropped thumbnails */}
      <div style={{ overflow: 'hidden' }}><img src={resolveUrl(rightMedia[0])} alt="" style={coverS(rightMedia[0])} loading="lazy" decoding="async" /></div>
      <div style={{ overflow: 'hidden', position: 'relative' }}>
        <img src={resolveUrl(rightMedia[1] || rightMedia[0])} alt="" style={coverS(rightMedia[1] || rightMedia[0])} loading="lazy" decoding="async" />
        {remainder > 0 && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(28,32,22,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#fff' }}>+{remainder}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Category Cover (non-photo stories) ─── */
function CategoryCover({ category, title }) {
  const config = getCategoryConfig(category);
  return (
    <div style={{
      width: '100%', aspectRatio: '3/2', background: config.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 32px', textAlign: 'center',
    }}>
      <span style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, color: config.fg, lineHeight: 1.3 }}>
        {title}
      </span>
    </div>
  );
}

/* ─── Engagement Tooltip ─── */
function EngagementTooltip({ storyId, type, children }) {
  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const handleEnter = () => {
    timerRef.current = setTimeout(() => {
      setShow(true);
      if (!data && !loading) {
        setLoading(true);
        getStoryEngagement(storyId)
          .then(setData)
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    }, 300);
  };

  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  let tooltipContent = null;
  if (show && data) {
    if (type === 'love') {
      const members = data.love?.members || [];
      tooltipContent = members.length > 0 ? (
        <div>
          {members.map((m, i) => (
            <div key={m.id || i} style={{ padding: '4px 0', fontSize: 13, color: '#2C2F26', fontFamily: FONT_B }}>
              {m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Someone'}
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 13, color: '#9AA088' }}>No one yet</div>;
    } else {
      const notes = data.notes || [];
      const recent = notes.slice(0, 3);
      tooltipContent = recent.length > 0 ? (
        <div>
          {recent.map((n, i) => (
            <div key={n.id || i} style={{ padding: '5px 0', borderBottom: i < recent.length - 1 ? '1px solid #F0EEE3' : 'none' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#2C2F26', fontFamily: FONT_B }}>{n.author?.name || `${n.asker?.firstName || ''} ${n.asker?.lastName || ''}`.trim() || 'Someone'}</div>
              <div style={{ fontSize: 13, color: '#5A6150', fontFamily: FONT_B, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{n.note || n.question_text || ''}</div>
            </div>
          ))}
          {notes.length > 3 && <div style={{ fontSize: 12, color: '#9AA088', paddingTop: 4 }}>+{notes.length - 3} more</div>}
        </div>
      ) : <div style={{ fontSize: 13, color: '#9AA088' }}>No messages yet</div>;
    }
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 8, background: '#fff', border: '1px solid #E4E1D2', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(40,46,28,.15)', padding: '10px 14px', minWidth: 160, maxWidth: 240,
          zIndex: 20, pointerEvents: 'none',
        }}>
          {loading ? <div style={{ fontSize: 13, color: '#9AA088' }}>Loading...</div> : tooltipContent}
          {/* Arrow */}
          <div style={{
            position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)',
            width: 10, height: 10, background: '#fff', border: '1px solid #E4E1D2',
            borderTop: 'none', borderLeft: 'none',
          }} />
        </div>
      )}
    </span>
  );
}

/* ─── Story Card ─── */
function StoryCard({ story, onClick }) {
  const author = story.author || {};
  const media = story.media || [];
  const verb = story.verb || (
    story.category === 'life_lesson' || story.category === 'wisdom' ? 'shared a life lesson'
    : story.category === 'milestone' ? 'marked a milestone'
    : 'shared a memory'
  );
  const dateStr = story.date ? new Date(story.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div onClick={onClick} role="link" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        background: '#fff', borderRadius: 18, border: '1px solid #EDEBDD', overflow: 'hidden',
        boxShadow: '0 10px 28px rgba(40,46,28,.1)', cursor: 'pointer',
        transition: 'transform .15s, box-shadow .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 18px 40px rgba(40,46,28,.16)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(40,46,28,.1)'; }}
    >
      {/* Author header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13, padding: '17px 20px 15px' }}>
        <div style={{ width: 46, height: 46, borderRadius: '50%', background: author.color || '#3E4F22', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_B }}>
          {author.initial || (author.name || 'U').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B }}>{author.name || 'Someone'}</span>
            {' '}<span style={{ fontSize: 15, color: '#8A917A', fontFamily: FONT_B }}>{verb}</span>
          </div>
          <div style={{ fontSize: 13.5, color: '#9AA088', marginTop: 2, fontFamily: FONT_B }}>
            {dateStr}
            {story.location && <>{' · '}<MapPin size={12} style={{ display: 'inline', verticalAlign: '-1px' }} /> {story.location}</>}
          </div>
        </div>
        <CategoryBadge category={story.category || 'family_memory'} />
      </div>

      {/* Title */}
      <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 25, color: '#2C2F26', padding: '0 20px', margin: '2px 0 13px', lineHeight: 1.15 }}>
        {story.title || 'Untitled'}
      </h3>

      {/* Media mosaic or category cover */}
      {media.length > 0 ? <MosaicPreview media={media} /> : <CategoryCover category={story.category || 'family_memory'} title={story.title} />}

      {/* Snippet */}
      {story.story && (
        <p style={{
          fontSize: 16, color: '#4a4f40', padding: '16px 20px 4px', fontFamily: FONT_B, lineHeight: 1.55, margin: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          "{story.story}"
        </p>
      )}

      {/* Engagement bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 20px 20px' }}>
        <EngagementTooltip storyId={story.id} type="love">
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, minHeight: 44,
            background: story.viewerSentLove ? '#FBECE4' : '#F2F1E7',
            color: story.viewerSentLove ? '#C0432C' : '#5A6150', fontFamily: FONT_B, cursor: 'default',
          }}>
            <Heart size={16} fill={story.viewerSentLove ? '#C0432C' : 'none'} /> {story.loveCount || 0}
          </span>
        </EngagementTooltip>
        <EngagementTooltip storyId={story.id} type="comments">
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, minHeight: 44,
            background: '#F2F1E7', color: '#5A6150', fontFamily: FONT_B, cursor: 'default',
          }}>
            <MessageCircle size={16} /> {story.commentCount || 0}
          </span>
        </EngagementTooltip>
        <button onClick={e => { e.stopPropagation(); onClick(); }} style={{
          marginLeft: 'auto', background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14,
          padding: '10px 20px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, minHeight: 44,
        }}>
          View & Comment
        </button>
      </div>
    </div>
  );
}

/* ─── Loading Skeleton ─── */
function SkeletonCard() {
  return (
    <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #EDEBDD', overflow: 'hidden', padding: 20 }}>
      <div style={{ display: 'flex', gap: 13, marginBottom: 16 }}>
        <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#E8E4D8' }} />
        <div style={{ flex: 1 }}>
          <div style={{ width: '60%', height: 16, background: '#E8E4D8', borderRadius: 8, marginBottom: 8 }} />
          <div style={{ width: '40%', height: 12, background: '#EFEEE5', borderRadius: 6 }} />
        </div>
      </div>
      <div style={{ width: '80%', height: 22, background: '#E8E4D8', borderRadius: 8, marginBottom: 16 }} />
      <div style={{ width: '100%', aspectRatio: '3/2', background: '#EFEEE5', borderRadius: 8, marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ width: 80, height: 36, background: '#EFEEE5', borderRadius: 999 }} />
        <div style={{ width: 60, height: 36, background: '#EFEEE5', borderRadius: 999 }} />
        <div style={{ marginLeft: 'auto', width: 130, height: 36, background: '#E8E4D8', borderRadius: 999 }} />
      </div>
      <style>{`@keyframes shimmer { 0% { opacity: .4 } 50% { opacity: 1 } 100% { opacity: .4 } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; } }`}</style>
    </div>
  );
}

/* ─── Timeline ─── */
export default function FeedTimeline() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const userData = getUser() || {};

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await axiosInstance.get('/dashboard/');
        const data = res.data;
        const rawStories = data.stories || [];
        const transformed = rawStories.map(s => {
          let media = (s.media_files || []).filter(m => m.media_type === 'image' || !m.media_type);
          if (media.length === 0 && s.thumbnailUrl) {
            const thumbUrl = s.thumbnailUrl.startsWith('/') ? `${API_BASE}${s.thumbnailUrl}` : s.thumbnailUrl;
            media = [{ id: 'thumb', url: thumbUrl, media_type: 'image' }];
          }
          const sharerName = s.sharer ? `${s.sharer.firstName || ''} ${s.sharer.lastName || ''}`.trim() : (userData.first_name || 'Someone');
          return {
            id: s.id,
            title: s.title || 'Untitled',
            story: s.preview || s.description || s.story || '',
            category: s.storyType || s.story_type || 'family_memory',
            date: s.sharedAt || s.created_at || s.date,
            location: s.location || '',
            author: {
              name: sharerName,
              initial: s.sharer?.initials || sharerName.charAt(0).toUpperCase(),
              color: s.sharer?.avatarColor || '#3E4F22',
            },
            media,
            loveCount: s.engagement?.reactionCount || 0,
            viewerSentLove: false,
            commentCount: s.engagement?.commentCount || 0,
          };
        });
        setStories(transformed);
      } catch (err) { console.error('Feed fetch error:', err); }
      finally { setLoading(false); }
    };
    fetchFeed();
  }, []);

  const buckets = groupByTimeBucket(stories);
  const totalNew = stories.length;

  if (loading) {
    return (
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '34px 24px 60px' }}>
        <FeedGreeting firstName={userData.first_name} />
        <SharePromptBar onShare={() => navigate('/tell-a-story')} />
        <FeedToolbar label="Recent" count={0} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingLeft: 28, borderLeft: '2px solid #E1DECB', marginLeft: 6 }}>
          <SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '34px 24px 60px' }}>
      <FeedGreeting firstName={userData.first_name} />

      {/* AI: Voice identification queue */}
      <IdentifyVoicesQueue />

      <SharePromptBar onShare={() => navigate('/tell-a-story')} />

      {/* AI: Wisdom moments feed */}
      <WisdomMomentsFeed />

      {stories.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 18, padding: '48px 24px', textAlign: 'center', border: '1px solid #EDEBDD', boxShadow: '0 10px 28px rgba(40,46,28,.1)' }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>📖</div>
          <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, color: '#2C2F26', margin: '0 0 8px' }}>No stories yet</h3>
          <p style={{ fontSize: 16, color: '#7C846A', margin: '0 0 20px', fontFamily: FONT_B }}>Be the first to share a memory with your family.</p>
          <button onClick={() => navigate('/tell-a-story')} style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 15, padding: '12px 24px', borderRadius: 999, border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(85,107,47,.26)' }}>Share a Story</button>
        </div>
      ) : (
        <>
          {buckets.map((bucket, bIdx) => (
            <div key={bucket.label}>
              {/* Toolbar for first bucket */}
              {bIdx === 0 && <FeedToolbar label={bucket.label} count={bucket.stories.length} />}

              {/* Bucket divider for subsequent buckets */}
              {bIdx > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '28px 0 18px 28px', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', background: '#C7C9B6', border: '3px solid #F5F5DC', zIndex: 1 }} />
                  <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#7C846A', margin: 0 }}>{bucket.label}</h2>
                  <span style={{ background: '#E7EEDA', color: '#556B2F', fontWeight: 700, fontSize: 13, padding: '3px 10px', borderRadius: 999, fontFamily: FONT_B }}>{bucket.stories.length} new</span>
                </div>
              )}

              {/* Stories */}
              <div style={{ borderLeft: '2px solid #E1DECB', marginLeft: 6, paddingLeft: 28, display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 24 }}>
                {bucket.stories.map((story, sIdx) => (
                  <div key={story.id} style={{ position: 'relative' }}>
                    <div style={{
                      position: 'absolute', left: -35, top: 28,
                      width: bIdx === 0 && sIdx === 0 ? 14 : 12,
                      height: bIdx === 0 && sIdx === 0 ? 14 : 12,
                      borderRadius: '50%',
                      background: bIdx === 0 && sIdx === 0 ? '#E65100' : '#B5C29A',
                      border: '3px solid #F5F5DC', zIndex: 1,
                    }} />
                    <StoryCard story={story} onClick={() => navigate(`/stories/${story.id}`)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Reduced motion */}
      <style>{`@media (prefers-reduced-motion: reduce) { * { transition: none !important; } }`}</style>
    </div>
  );
}

export { FeedGreeting, SharePromptBar, FeedToolbar, StoryCard, MosaicPreview, CategoryBadge };
